import {
  formatDate,
  formatFullFloat,
  formatFullInt,
  formatPercentWithoutUnit,
  formatScore,
  isNumber,
} from '../helpers/numbers';
import Funds from '../pia/funds/types';
import Pia from '../pia/type';
import fundFullFloatKeys from './fundFloatKeys';
import fundFullIntKeys from './fundIntKeys';
import fundPercentKeys from './fundPercentKeys';
import { LotResult } from './screener/screenerTypes';

function isDwellingRow(
  value: Pia.RehanDwellingRows | Pia.RehanBuildingRows
): value is Pia.RehanDwellingRows {
  return (value as Pia.RehanDwellingRows).building_id !== undefined;
}

function isDwellingRows(
  value: Pia.RehanBuildingRows[] | Pia.RehanDwellingRows[]
): value is Pia.RehanDwellingRows[] {
  return value.reduce((acc, row) => {
    return acc && isDwellingRow(row);
  }, true);
}

function isBuildingRow(
  value: Pia.RehanBuildingRows | Pia.RehanDwellingRows
): value is Pia.RehanBuildingRows {
  return (value as Pia.RehanBuildingRows).lot_id !== undefined;
}

function isBuildingRows(
  value: Pia.RehanBuildingRows[] | Pia.RehanDwellingRows[]
): value is Pia.RehanBuildingRows[] {
  return value.reduce((acc, row) => {
    return acc && isBuildingRow(row);
  }, true);
}

export function formatComparables(rows: {
  comparables: Pia.RehanBuildingRows[] | Pia.RehanDwellingRows[];
}) {
  const tmp = rows?.comparables;
  if (isDwellingRows(tmp)) {
    return tmp.map((dwelling) => formatRehanDwellingRow(dwelling, false));
  }
  if (isBuildingRows(tmp)) {
    return tmp.map(formatRehanBuildingRow);
  }
}

export function formatDwellingsRow(rows: { dwellings: Pia.DwellingRows[] }) {
  return rows?.dwellings.map((dwelling) => formatDwellingRow(dwelling));
}

export function formatSameBuildingDwellingsRow(rows: { dwellings: Pia.DwellingRows[] }) {
  return rows?.dwellings.map((dwelling) => formatDwellingRow(dwelling, true));
}

function formatTransactionAddress(toFormat: Funds.Transactions) {
  return {
    label: `${formatLabelWithSeperator([toFormat.street_short, toFormat.nb], ' ')}`,
    link: `/asset/buildings/${toFormat.building_id}`,
  };
}

function formatTransactionRow(
  transaction: Funds.Transactions & Funds.TransactionGroup & Pia.BuildingRows
) {
  const { construction_date, date, value, ground_area, total_area, ...rest } = transaction;
  return {
    ...rest,
    street_short: formatTransactionAddress(transaction),
    ground_area: formatFullInt(ground_area),
    total_area: formatFullInt(Number(total_area)),
    tx_year: date ? new Date(date).getFullYear() : null,
    construction_year: construction_date ? new Date(construction_date).getFullYear() : null,
    tx_price: formatFullInt(value),
  };
}

function formatNumberListFromObject(
  fn: typeof formatFullInt | typeof formatPercentWithoutUnit | typeof formatFullFloat
) {
  return function formater<T extends unknown>(keys: (keyof T)[], object: T) {
    return keys.reduce((result, key) => {
      const value = object[key];
      return {
        ...result,
        [key]:
          value !== null && value !== undefined && value !== '' && isNumber(Number(value))
            ? fn(Number(value))
            : null,
      };
    }, {} as Partial<T>);
  };
}

const formatFloatFromObject = formatNumberListFromObject(formatFullFloat);
const formatFullIntsFromObject = formatNumberListFromObject(formatFullInt);

const formatPercentFromObject = formatNumberListFromObject(formatPercentWithoutUnit);

function formatFundName(toFormat: Pia.FundsRows) {
  return {
    label: toFormat.official_short_name ?? '-',
    link: `/funds/${toFormat.id}`,
  };
}

export function formatTransactionRows({
  transactions,
}: { transactions?: (Funds.Transactions & Funds.TransactionGroup & Pia.BuildingRows)[] } = {}) {
  return transactions?.map(formatTransactionRow);
}

function formatFundRow(fund: Pia.FundsRows) {
  const {
    official_short_name,
    reported_last_report_date,
    official_launch_date,
    computed_median_maturity,
    computed_weighted_average_maturity,
    reported_wault,
    market_dividend_date,
    official_press_score_environmental,
    official_press_score_esg,
    official_press_score_governance,
    official_press_score_social,
    estimated_total_net_rent,
    ...rest
  } = fund;
  const formatedLastReportDate = reported_last_report_date
    ? new Date(reported_last_report_date)
    : null;
  const formatedDividendDate = market_dividend_date ? new Date(market_dividend_date) : null;

  return {
    ...rest,
    ...formatFloatFromObject(fundFullFloatKeys, fund),
    ...formatPercentFromObject(fundPercentKeys, fund),
    ...formatFullIntsFromObject(fundFullIntKeys, fund),
    official_short_name: formatFundName(fund),
    official_launch_date: official_launch_date
      ? new Date(official_launch_date).getFullYear()
      : null,
    reported_last_report_date: formatedLastReportDate ? formatDate(formatedLastReportDate) : null,
    market_dividend_date: formatedDividendDate ? formatDate(formatedDividendDate) : null,
    official_press_score_environmental: official_press_score_environmental
      ? formatScore(official_press_score_environmental)
      : null,
    official_press_score_esg: official_press_score_esg
      ? formatScore(official_press_score_esg)
      : null,
    official_press_score_governance: official_press_score_governance
      ? formatScore(official_press_score_governance)
      : null,
    official_press_score_social: official_press_score_social
      ? formatScore(official_press_score_social)
      : null,
  };
}

export function formatFundRows({ funds }: { funds?: Pia.FundsRows[] } = {}) {
  return funds?.map(formatFundRow);
}

function formatDwellingRow(result: Pia.DwellingRows, isSameBuilding?: boolean) {
  const {
    id,
    ewid,
    street_short,
    nb,
    construction_date,
    potential_net_rent,
    potential_charges,
    real_net_rent,
    real_charges,
    area,
    estimated_area,
    potential_net_rent_per_m2,
    potential_charges_per_m2,
    potential_market_value,
    potential_market_value_per_m2,
    potential_roa,
    estimated_co2_emission_per_m2,
    estimated_idc_per_m2,
    labels,
    ...rest
  } = result;

  const label = formatLabelWithSeperator([street_short, nb], ' ');
  // if isSameBuilding is true, we don't want to display the link
  // to the dwelling on the address but on the ewid as for most of
  // the case street_short is the same for all dwellings of a building

  return {
    id,
    ewid: isSameBuilding
      ? { label: ewid?.toString() ?? '-', link: `/asset/dwellings/${id}` }
      : ewid,
    street_short: isSameBuilding ? label : { label: label, link: `/asset/dwellings/${id}` },
    construction_date: construction_date ? new Date(construction_date).getFullYear() : null,
    potential_net_rent: formatFullInt(potential_net_rent || null),
    potential_net_rent_monthly: formatFullInt(potential_net_rent ? potential_net_rent / 12 : null),
    potential_charges: formatFullInt(potential_charges || null),
    potential_charges_monthly: formatFullInt(potential_charges ? potential_charges / 12 : null),
    real_net_rent: formatFullInt(real_net_rent || null),
    real_net_rent_monthly: formatFullInt(real_net_rent ? real_net_rent / 12 : null),
    real_charges: formatFullInt(real_charges || null),
    real_charges_monthly: formatFullInt(real_charges ? real_charges / 12 : null),
    area: formatFullInt(area),
    estimated_area: formatFullInt(estimated_area),
    potential_net_rent_per_m2: formatFullInt(potential_net_rent_per_m2),
    potential_charges_per_m2: formatFullInt(potential_charges_per_m2),
    potential_market_value: formatFullInt(potential_market_value),
    potential_market_value_per_m2: formatFullInt(potential_market_value_per_m2),
    potential_roa: formatPercentWithoutUnit(potential_roa),
    estimated_co2_emission_per_m2: formatFullFloat(estimated_co2_emission_per_m2),
    estimated_idc_per_m2: formatFullFloat(estimated_idc_per_m2),
    labels: labels?.join(', '),
    ...rest,
  };
}

export function formatRehanDwellingRow(result: Pia.RehanDwellingRows, isSameBuilding?: boolean) {
  const {
    id,
    official_ewid,
    official_street_short,
    official_address_nb,
    official_construction_date,
    estimated_net_rent,
    estimated_charges,
    listed_net_rent,
    listed_charges,
    official_area,
    computed_total_area,
    estimated_net_rent_per_m2,
    estimated_charges_per_m2,
    estimated_market_value,
    estimated_market_value_per_m2,
    estimated_roa,
    estimated_co2_scope_1_2_per_m2,
    estimated_energy_intensity,
    official_minergie_labels,
    estimated_net_rent_monthly,
    estimated_charges_monthly,
    listed_net_rent_monthly,
    listed_charges_monthly,
    ...rest
  } = result;

  const label = formatLabelWithSeperator([official_street_short, official_address_nb], ' ');
  // if isSameBuilding is true, we don't want to display the link
  // to the dwelling on the address but on the ewid as for most of
  // the case street_short is the same for all dwellings of a building

  return {
    id,
    official_ewid: isSameBuilding
      ? { label: official_ewid?.toString() ?? '-', link: `/asset/dwellings/${id}` }
      : official_ewid,
    official_street_short: isSameBuilding
      ? label
      : { label: label, link: `/asset/dwellings/${id}` },
    official_construction_date: official_construction_date
      ? new Date(official_construction_date).getFullYear()
      : null,
    estimated_net_rent: formatFullInt(estimated_net_rent ?? null),
    estimated_charges: formatFullInt(estimated_charges ?? null),
    listed_net_rent: formatFullInt(listed_net_rent ?? null),
    listed_net_rent_monthly: formatFullInt(listed_net_rent_monthly ?? null),
    listed_charges: formatFullInt(listed_charges ?? null),
    listed_charges_monthly: formatFullInt(listed_charges_monthly ?? null),
    official_area: formatFullInt(official_area ?? null),
    computed_total_area: formatFullInt(computed_total_area ?? null),
    estimated_net_rent_per_m2: formatFullInt(estimated_net_rent_per_m2 ?? null),
    estimated_charges_per_m2: formatFullInt(estimated_charges_per_m2 ?? null),
    estimated_market_value: formatFullInt(estimated_market_value ?? null),
    estimated_market_value_per_m2: formatFullInt(estimated_market_value_per_m2 ?? null),
    estimated_roa: formatPercentWithoutUnit(estimated_roa),
    estimated_co2_scope_1_2_per_m2: formatFullFloat(estimated_co2_scope_1_2_per_m2),
    estimated_energy_intensity: formatFullFloat(estimated_energy_intensity),
    official_minergie_labels: official_minergie_labels?.join(', '),
    estimated_net_rent_monthly: formatFullFloat(estimated_net_rent_monthly),
    estimated_charges_monthly: formatFullFloat(estimated_charges_monthly),
    ...rest,
  };
}

function isGroup(
  item: Funds.BuildingGroupRows | Funds.BuildingRows
): item is Funds.BuildingGroupRows {
  return (item as Funds.BuildingGroupRows).fund_id !== undefined;
}

export function formatFundBuildingsRow(rows: { buildings: Funds.BuildingRows[] }) {
  return rows?.buildings.map((item) => formatFundBuilding(item));
}

export function formatFundBuildingGroups(rows: { building_groups: Funds.BuildingGroupRows[] }) {
  return rows?.building_groups.map((item) => formatFundBuilding(item));
}

function formatFundBuildingAddress(toFormat: Funds.BuildingGroupRows | Funds.BuildingRows) {
  if (isGroup(toFormat)) return toFormat.reported_label;
  const labelArr = [toFormat.official_street, toFormat.official_address_nb].filter(Boolean);
  return {
    label: labelArr.length > 0 ? labelArr.join(' ') : '-',
    link: `/asset/buildings/${toFormat.building_id}`,
  };
}

function formatFundBuilding(toFormat: Funds.BuildingGroupRows | Funds.BuildingRows) {
  return {
    ...toFormat,
    total_area: formatFullInt(toFormat.reported_total_area),
    potential_roa: toFormat.estimated_roa ? formatPercentWithoutUnit(toFormat.estimated_roa) : null,
    actual_market_value: formatFullInt(toFormat.reported_market_value),
    cost_price: formatFullInt(toFormat.reported_cost_price),
    actual_rental_status_target: formatFullInt(toFormat.reported_target_rent),
    actual_target_roa: toFormat.reported_target_roa
      ? formatPercentWithoutUnit(toFormat.reported_target_roa)
      : null,
    actual_rental_status_real: formatFullInt(toFormat.reported_real_rent),
    actual_real_roa: toFormat.reported_real_roa
      ? formatPercentWithoutUnit(toFormat.reported_real_roa)
      : null,
    label: formatFundBuildingAddress(toFormat),
    construction_date: toFormat.official_construction_date
      ? new Date(toFormat.official_construction_date).getFullYear()
      : null,
    type: toFormat.official_type,
    locality: isGroup(toFormat) ? toFormat.reported_locality : toFormat.official_locality,
    potential_market_value: formatFullInt(
      isGroup(toFormat) ? toFormat.estimated_total_market_value : toFormat.estimated_market_value
    ),
    potential_net_rent: formatFullInt(toFormat.estimated_total_net_rent),
    potential_charges: formatFullInt(
      isGroup(toFormat) ? toFormat.estimated_charges : toFormat.estimated_total_net_charges
    ),
  };
}

export function formatBuildingsRow(rows: { buildings: Pia.BuildingRows[] }) {
  return rows?.buildings.map(formatBuildingRow);
}

// we keep it for now, after all the backend is migrated, this can be deleted
function formatBuildingRow(building: Pia.BuildingRows) {
  const {
    id,
    street_short,
    nb,
    construction_date,
    potential_roa,
    real_roa,
    total_area,
    ground_area,
    dwellings_nb,
    residential_area,
    commercial_area,
    estimated_co2_emission_per_m2,
    estimated_idc_per_m2,
    potential_market_value,
    potential_net_rent,
    potential_charges,
    real_market_value,
    real_net_rent,
    real_charges,
    potential_net_rent_per_m2,
    potential_charges_per_m2,
    potential_market_value_per_m2,
    potential_gross_rent,
    potential_gross_rent_per_m2,
    labels,
    type,

    solar_existing,
    fund_legal_forms,
    r_market_value,
    r_real_rent,
    r_target_rent,
    r_rental_space,
    r_cost_price,
    r_gross_yield,
    ...rest
  } = building;

  return {
    id,
    street_short: {
      label: `${formatLabelWithSeperator([street_short, nb], ' ')}`,
      link: `/asset/buildings/${id}`,
    },
    construction_date: construction_date ? new Date(construction_date).getFullYear() : null,
    potential_roa: building.potential_roa ? formatPercentWithoutUnit(building.potential_roa) : null,
    real_roa: building.real_roa ? formatPercentWithoutUnit(building.real_roa) : null,
    total_area: formatFullInt(Number(total_area)),
    ground_area: formatFullInt(ground_area),
    dwellings_nb: formatFullInt(dwellings_nb),
    residential_area: formatFullInt(residential_area),
    commercial_area: formatFullInt(Number(commercial_area)),
    estimated_co2_emission_per_m2: formatFullFloat(estimated_co2_emission_per_m2),
    estimated_idc_per_m2: formatFullFloat(estimated_idc_per_m2),
    potential_market_value: formatFullInt(potential_market_value),
    potential_net_rent: formatFullInt(potential_net_rent),
    potential_charges: formatFullInt(potential_charges),
    real_market_value: formatFullInt(real_market_value),
    real_net_rent: formatFullInt(real_net_rent),
    real_charges: formatFullInt(real_charges),
    potential_net_rent_per_m2: formatFullInt(potential_net_rent_per_m2),
    potential_charges_per_m2: formatFullInt(potential_charges_per_m2),
    potential_market_value_per_m2: formatFullInt(potential_market_value_per_m2),
    potential_gross_rent: formatFullInt(potential_gross_rent),
    potential_gross_rent_per_m2: formatFullInt(potential_gross_rent_per_m2),
    labels: labels?.join(', '),
    solar_existing: formatFullInt(solar_existing),
    type: `buildings.type.${type}`,
    fund_legal_forms: fund_legal_forms?.map((legal_form) => `table.fund_legal_form.${legal_form}`),
    r_market_value: formatFullInt(r_market_value),
    r_real_rent: formatFullInt(r_real_rent),
    r_target_rent: formatFullInt(r_target_rent),
    r_rental_space: formatFullInt(r_rental_space),
    r_cost_price: formatFullInt(r_cost_price),
    r_gross_yield: formatPercentWithoutUnit(r_gross_yield),
    ...rest,
  };
}

export function formatRehanBuildingRow(building: Pia.RehanBuildingRows) {
  const {
    id,
    official_street_short,
    official_address_nb,
    official_construction_date,
    estimated_roa,
    reported_roa,
    computed_total_area,
    official_ground_area,
    official_dwellings_nb,
    computed_total_dwellings_area,
    computed_total_commercial_area,
    estimated_co2_scope_1_2_per_m2,
    estimated_energy_intensity,
    estimated_market_value,
    estimated_total_net_rent,
    estimated_total_net_charges,
    reported_market_value,
    listed_total_net_rent,
    listed_total_charges,
    estimated_total_net_rent_per_m2,
    estimated_total_net_charges_per_m2,
    estimated_market_value_per_m2,
    official_minergie_labels,
    official_total_electricity_yield_kwh_per_year,
    official_installed_solar_power,
    reported_real_rent,
    reported_target_rent,
    reported_rental_area,
    reported_cost_price,
    reported_gross_yield,
    ...rest
  } = building;

  return {
    id,
    official_street_short: {
      label: `${formatLabelWithSeperator([official_street_short, official_address_nb], ' ')}`,
      link: `/asset/buildings/${id}`,
    },
    official_construction_date: official_construction_date
      ? new Date(official_construction_date).getFullYear()
      : null,
    estimated_roa: formatPercentWithoutUnit(estimated_roa ?? null),
    reported_roa: formatPercentWithoutUnit(reported_roa ?? null),
    computed_total_area: formatFullInt(computed_total_area ?? null),
    official_ground_area: formatFullInt(official_ground_area ?? null),
    official_dwellings_nb: formatFullInt(official_dwellings_nb ?? null),
    computed_total_dwellings_area: formatFullInt(computed_total_dwellings_area ?? null),
    computed_total_commercial_area: formatFullInt(computed_total_commercial_area ?? null),
    estimated_co2_scope_1_2_33_per_m2: formatFullFloat(estimated_co2_scope_1_2_per_m2 ?? null),
    estimated_energy_intensity: formatFullFloat(estimated_energy_intensity || null),
    estimated_market_value: formatFullInt(estimated_market_value ?? null),
    estimated_total_net_rent: formatFullInt(estimated_total_net_rent ?? null),
    estimated_total_net_charges: formatFullInt(estimated_total_net_charges ?? null),
    listed_total_net_rent: formatFullInt(listed_total_net_rent ?? null),
    listed_total_charges: formatFullInt(listed_total_charges ?? null),
    estimated_total_net_rent_per_m2: formatFullInt(estimated_total_net_rent_per_m2 ?? null),
    estimated_total_net_charges_per_m2: formatFullInt(estimated_total_net_charges_per_m2 ?? null),
    estimated_market_value_per_m2: formatFullInt(estimated_market_value_per_m2 ?? null),
    official_minergie_labels: official_minergie_labels?.join(', '),
    official_total_electricity_yield_kwh_per_year: formatFullInt(
      official_total_electricity_yield_kwh_per_year ?? null
    ),
    reported_market_value: formatFullInt(reported_market_value ?? null),
    reported_real_rent: formatFullInt(reported_real_rent ?? null),
    reported_target_rent: formatFullInt(reported_target_rent ?? null),
    reported_rental_area: formatFullInt(reported_rental_area ?? null),
    reported_cost_price: formatFullInt(reported_cost_price ?? null),
    reported_gross_yield: formatPercentWithoutUnit(reported_gross_yield ?? null),
    official_installed_solar_power: formatFullFloat(official_installed_solar_power ?? null),
    ...rest,
  };
}

export function formatLotsRow(rows: { lots: LotResult[] }) {
  return rows.lots.map(formatLotRow);
}

function formatLotRow(lots: LotResult) {
  const { id, official_egrid, official_area, ...rest } = lots;

  return {
    id,
    official_egrid: {
      label: official_egrid ?? '-',
      link: `/asset/lots/${id}`,
    },
    official_area: formatFullFloat(official_area),
    ...rest,
  };
}

export type FormatedLotRow = ReturnType<typeof formatLotRow>;
export type FormatedFundBuildingRow = ReturnType<typeof formatFundBuilding>;
export type FormatedBuildingRow = ReturnType<typeof formatBuildingRow>;
export type FormatedDwellingRow = ReturnType<typeof formatDwellingRow>;

export function formatLabelWithSeperator(
  stringArr: Array<string | number | null | undefined>,
  seperator: string
) {
  const validContent = stringArr.filter(Boolean);
  return validContent.length ? validContent.join(seperator) : '-';
}
